import type { CSSProperties } from 'react';

import type {
  RollingOfferAnimations,
  RollingOfferMilestoneState,
} from '../../components/EventTriggerDialog/offers/RollingOffersLayout/types';
import type { SupportedLanguages } from '../../i18n/constants';
import type { CoinType } from '../cointype/CoinTypeStore';
import type { PlayerQuest, PlayerQuestCompletionReward } from '../quest/types';
import type { BundleItem } from '../rewards/types';

export enum EVENT_TRIGGER_LAYOUT_VERSIONS {
  V1 = '1',
  V2 = '2',
}

export type MessageElementType = 'ACTION_BTN' | 'TIMER' | 'CLOSE' | 'TITLE' | 'TEXT';
export type GiftElementType =
  | 'ACTION_BTN'
  | 'REWARD'
  | 'FRB_REWARD'
  | 'SNAPPY_GIFT'
  | 'METAGAME_FEATURE_REWARD'
  | 'RIPS_PACK_REWARD';
export type OfferElementType =
  | 'ACTION_BTN'
  | 'BUY_ALL_BTN'
  | 'CUT_PRICE'
  | 'REWARD'
  | 'CLOSE'
  | 'TIMER'
  | 'TERMS_LINK'
  | 'EXTRA_PERCENT'
  | 'DECLINE_BTN'
  | 'EXTRA_OFFER';
export type QuestElementType =
  | 'ACTION_BTN'
  | 'DYNAMIC_BTN'
  | 'REWARD'
  | 'CLOSE'
  | 'TIMER'
  | 'DYNAMIC_IMAGE_1'
  | 'DYNAMIC_TEXT_1'
  | 'FRB_REWARD'
  | 'DYNAMIC_IMAGE'
  | 'METAGAME_REWARD'
  | 'CHALLENGE_REWARD';

export type RollingOfferElementType =
  | 'ACTION_BTN'
  | 'BUY_ALL_BTN'
  | 'CUT_PRICE'
  | 'REWARD'
  | 'CLOSE'
  | 'TIMER'
  | 'DYNAMIC_IMAGE'
  | 'CHECK'
  | 'TERMS_LINK'
  | 'CHALLENGE_REWARD';
export type ElementType =
  | MessageElementType
  | GiftElementType
  | OfferElementType
  | QuestElementType
  | RollingOfferElementType;

export type GameID = number;
export type MessageCtaPopupType = 'INVITE_FRIEND' | 'STORE' | 'REDEEM' | 'BINGO' | 'RIPS';
export type MessageKind = 'invite_friends';
export type RaffleStage = 'announcement' | 'entry' | 'winner' | 'loser';

type CtaActionValueMap = {
  NONE: never;
  GAME: GameID;
  POPUP: MessageCtaPopupType;
  CHAT: never;
  SET_COIN_MODE: CoinType;
  EXTERNAL_LINK: string;
  GAME_UI_PATH: string;
  ENTER_RAFFLE: string;
};

type CtaActionValue =
  | {
      [T in keyof CtaActionValueMap]: {
        ctaType: T;
        ctaValue?: CtaActionValueMap[T];
      };
    }[keyof CtaActionValueMap]
  | {
      ctaType: object; // Not really object, just to cover all the possibly unknown types
      ctaValue?: unknown;
    };

export type ElementProps<T = any> = {
  type: ElementType;
  left: number;
  top: number;
  width: number;
  height: number;
  view?: string;
  text?: string;
  btnType?: 'base' | 'main' | 'light' | 'orange' | 'blue';
  style?: CSSProperties;
  additionalData?: T;
  imageUrl?: string;
};

export type ElementDesign = 'design_1' | 'design_2';

export type OfferElementsProps = ElementProps<{
  packageId: number;
  hidePrice?: boolean;
  design?: ElementDesign;
  link?: string;
  countriesCode?: string[];
  bundleItemId?: string;
  cutOffPrice?: number;
  stockText?: string;
}>;
export type RollingOfferElementsProps = ElementProps & {
  packageId?: number;
  hidePrice?: boolean;
  design?: ElementDesign;
  bundleItemId?: string;
  link?: string;
  countriesCode?: string[];
  animation?: {
    animationReverse?: boolean;
    animationEnabled?: boolean;
    onAnimationEnd?: () => void;
  };
};

export type QuestElementsProps = ElementProps & {
  imageUrl?: string;
  moreDetails?: {
    order: number;
  };
};

export type EnhancedElementProps = ElementProps & {
  externalLink?: string;
};

type MandatoryMessageElements = {
  ACTION_BTN: ElementProps;
  CLOSE: ElementProps;
};

type OptionalMessageElements = Partial<{
  TITLE: ElementProps;
  TEXT: ElementProps;
  TIMER: ElementProps;
}>;

export type MessageLayoutElements = MandatoryMessageElements & OptionalMessageElements;

export type OfferLayoutElements = Partial<Record<OfferElementType, ElementProps>>;

export type GiftLayoutElements = Record<GiftElementType, ElementProps>;

export type QuestLayoutElements = Record<QuestElementType, ElementProps>;

export type Elements = MessageLayoutElements | OfferLayoutElements | GiftLayoutElements | QuestLayoutElements;

export type BenefitLayoutV2 = {
  elements: Elements;
  attributes: {
    imageWidth: number;
    imageHeight: number;
  };
  version: string;
};

export type BenefitLayoutV3 = {
  elements: ElementProps[];
  attributes: {
    imageWidth: number;
    imageHeight: number;
  };
  version: string;
};

export type LayoutType = BenefitLayoutV2 | BenefitLayoutV3;

export interface LocalizedEventElement<T extends LayoutType> {
  layout: T;
  storeLayout?: T;
  imageUrl: string;
  miniImageUrl?: string;
  infoImageUrl?: string;
  storeLayoutImageUrl?: string;
  riveFileUrl?: string;
  riveLayout?: string;
}

export type LocalizedEventElements<T extends LayoutType> = Partial<
  Record<SupportedLanguages, LocalizedEventElement<T>>
>;

export type LocalizedEventElementsType<T extends LayoutType> = {
  localizedElements?: LocalizedEventElements<T>;
};

export type BasePopupType = {
  key: string;
  imageUrl: string;
  riveFileUrl?: string;
  riveLayout?: string;
};

// Message
export type MessagePopupType = BasePopupType & {
  layout: BenefitLayoutV2 & LocalizedEventElementsType<BenefitLayoutV2>;
  miniImageUrl: string;
  kind?: MessageKind | null;
  raffleId?: string;
  raffleStage?: RaffleStage | null;
} & CtaActionValue;

// Offer
export type OfferPopupType = BasePopupType & {
  price: number;
  miniImageUrl: string;
  layout: OfferLayoutElements | BenefitLayoutV2;
  gcCoin: number;
  scCoin: number;
  packageId: number;
  bundleItems?: BundleItem[];
  termsLink: string;
  wonExtraPercent?: number;
};

// 2-3 Offer
export type OfferPackage = {
  packageId: number;
  price: number;
  gcCoin: number;
  scCoin: number;
  bundleItems?: BundleItem[];
  isBuyAll?: boolean;
};

export type BenefitPackage = {
  id: string;
  benefitKey: string;
  benefitType: string;
  createdAt: string;
  delay: number;
  order: number;
  packageId: number;
  timeLeft: number;
  updatedAt: string;
  isActive: boolean;
  isClaimed: boolean;
  isLocked: boolean;
  isBuyAll?: boolean;
};

export type OfferVisibility = { lobby: boolean; store: boolean };

// Mirrors OfferMode in db-blueprint's offers model — keep the members in step with it.
export type OfferMode = 'STANDARD' | 'TAKE_OR_LOSE' | 'LIMITED';

export type OfferV2PopupType = BasePopupType & {
  miniImageUrl: string;
  packages: OfferPackage[];
  layout: BenefitLayoutV3 & LocalizedEventElementsType<BenefitLayoutV3>;
  storeLayout?: BenefitLayoutV3;
  storeLayoutImageUrl?: string;
  visibility?: OfferVisibility;
  termsLink: string;
  wonExtraPercent?: number;
  isTakeOrLose?: boolean;
  mode?: OfferMode;
};

export type RollingOfferPopupType = OfferV2PopupType & {
  offerType: string;
  currentStep: number;
  benefitPackages: BenefitPackage[];
  packages: OfferPackage[];
};

// Gift
export type GiftPopupType = BasePopupType & {
  layout: (GiftLayoutElements | BenefitLayoutV2) & LocalizedEventElementsType<BenefitLayoutV2>;
  gcCoin: number;
  scCoin: number;
  freeSpinsImageUrl?: string;
  packageId?: number;
  bundleItems?: BundleItem[];
  raffleId?: string;
  raffleStage?: RaffleStage | null;
};

// Coin Back Booster
export type CoinBackBoosterPopupType = {
  benefitType: string;
  key: string;
  multiplier: number;
  widgetImageUrl: string;
};

// Personal Gift
export type PersonalGiftPopupType = {
  userGift: {
    id: string;
  };
  benefitType: 'personalGift';
  gcCoin: number;
  scCoin: number;
  key: string;
  imageUrl: string;
  layout: BenefitLayoutV2;
  packageId?: number;
  bundleItems?: BundleItem[];
  raffleId?: string;
  raffleStage?: RaffleStage | null;
};

// Quest
export type QuestPopupType = {
  benefitType: 'quest';
  key: string;
  imageUrl: string;
  widgetImageUrl: string;
  layout: BenefitLayoutV2 & LocalizedEventElementsType<BenefitLayoutV2>;
  rewardPackage: PlayerQuestCompletionReward;
} & PlayerQuest;

export const isEventTriggerLayoutV2 = (
  layout: OfferLayoutElements | GiftLayoutElements | BenefitLayoutV2,
): layout is BenefitLayoutV2 => (layout as BenefitLayoutV2).elements !== undefined;

export function isQuestLayoutElements(elements: any): elements is QuestLayoutElements {
  return elements && typeof elements.CLOSE !== 'undefined';
}

export const isEventTriggerLayoutV3 = (layout: BenefitLayoutV3): layout is BenefitLayoutV3 =>
  Array.isArray(layout.elements);

// WheelOfFortune
export type WheelOfFortuneSlice = {
  gcAmount: number;
  scAmount: number;
};

export type WheelOfFortunePayload = {
  key: string;
  winnerSlice: number;
  isFree: boolean;
  slices: WheelOfFortuneSlice[];
};

export type DialogProps = {
  onClose: VoidFunction;
  onPurchase?: VoidFunction;
  onClaim?: VoidFunction;
  onLayoutClick: VoidFunction;
};

export type RoadUserState = {
  currentOrder: number;
  currentFreeStepId?: string;
  currentPaidStepId?: string | null;
  isUserPaidForLane: boolean;
  currentStepClaimDate: string; //Date string in ISO format
  isUserInLastStep: boolean;
};

export type RoadStepPackage = {
  price: number;
  gcCoin: number;
  scCoin: number;
  bundleItems: BundleItem[] | null;
  packageId: number;
};

export type RoadStep = {
  id: string;
  order: number;
  delay: number;
  packageImageUrl: string;
  backgroundImageUrl: string;
  package: RoadStepPackage;
  isPackageContentHidden: boolean;
};

export type RoadLane = {
  id: string;
  order: number;
  label: string;
  paid: boolean;
  backgroundImageUrl: string;
  steps: RoadStep[];
  package: {
    price: number;
    gcCoin: number;
    scCoin: number;
    bundleItems: BundleItem[] | null;
    packageId: number;
  };
};

export enum RoadLocalizedAssetKey {
  HERO = 'HERO',
  INFO = 'INFO',
  WIDGET = 'WIDGET',
}

export type RoadLocalizedAsset = {
  key: RoadLocalizedAssetKey;
  url: string;
  language: SupportedLanguages;
};

export type RoadPopupType = {
  benefitType: 'road';
  key: string;
  lanes: RoadLane[];
  name: string;
  createdAt: string;
  description: string;
  addedById: number;
  assets: RoadLocalizedAsset[];
  updatedAt: string;
  id: string;
};

export enum EVENT_TRIGGER_ACTION_TYPE {
  COIN_TUTORIAL = 'COIN_TUTORIAL',
}

export type ActionPopupType = {
  benefitType: 'action';
  key: string;
};

export type EventTriggerMapping = {
  offer: OfferPopupType;
  gift: GiftPopupType;
  wheelOfFortune: WheelOfFortunePayload;
  message: MessagePopupType;
  personalGift: PersonalGiftPopupType;
  '2-3-offer': OfferV2PopupType;
  rollingOffer: RollingOfferPopupType;
  coinBackBooster: CoinBackBoosterPopupType;
  quest: QuestPopupType;
  road: RoadPopupType;
  action: ActionPopupType;
};

export type EventTriggerPopupType = keyof EventTriggerMapping;
export type EventTriggerPopupTypeWithLayout = keyof Omit<
  EventTriggerMapping,
  'offer' | 'wheelOfFortune' | 'personalGift' | 'coinBackBooster' | 'quest' | 'road' | 'action'
>;

export type EventTriggerPayloadType<T extends EventTriggerPopupType> = EventTriggerMapping[T];

export type Benefit<T extends EventTriggerPopupType> = {
  key: string;
  benefitType: EventTriggerPopupType;
  imageUrl?: string;
  infoImageUrl?: string;
  visibility?: OfferVisibility;
  riveFileUrl?: string;
  riveLayout?: string;
} & EventTriggerPayloadType<T>;

export type EventTriggerUniques = {
  eventTriggerId: string;
  audienceId?: string;
  audience?: object;
  triggerKey: string;
  conditionsKey: string;
  weight: number;
  hideWidgetTimer: boolean;
  timer?: number | null;
  purchaseCooldown?: number;
  usesPerUserLimit?: number;
  type: EventTriggerPopupType;
  rewardClaimEndDate?: Date;
  startDate?: Date | string;
  endDate?: Date;
  extra: {
    coinMode?: 'gc' | 'sc' | 'all';
    actionType?: EVENT_TRIGGER_ACTION_TYPE;
    isLimitedOffer?: boolean;
    limitedOffer?: {
      displayCap: number;
      soldOutAt: string;
    };
  };
};

export type RollingOfferPacksState = BenefitPackage & {
  state: RollingOfferMilestoneState;
  animateLockOnLoad?: boolean;
};

export type RollingOfferExtraData = {
  defaultState: Map<string, RollingOfferPacksState>;
  defaultAnimations: Map<string, RollingOfferAnimations>;
};

export type RoadExtraData = {
  successfulPurchase: boolean;
};

export type EventTriggerPopupExtraData<T extends EventTriggerPopupType> = T extends 'rollingOffer'
  ? RollingOfferExtraData
  : T extends 'road'
    ? RoadExtraData
    : Record<string, never>;

export type EventTriggerPopupData = {
  expiredAt?: Date;
  canDisplay: boolean;
  userEventTriggerId: string;
  eventTrigger: EventTriggerUniques;
  benefit: Benefit<EventTriggerPopupType>;
  benefitKey?: string;
  userGift?: {
    id: string;
  };
  currentStep?: number;
  extraData?: EventTriggerPopupExtraData<EventTriggerPopupType>;
  isNewEventType?: boolean;
  roadState?: RoadUserState;
  rewardClaimEndDate?: Date;
  isInClaimPeriod?: boolean;
};

export interface EventTriggersResponse {
  currentEventsByTrigger: EventTriggerPopupData[];
  otherActiveEvents: EventTriggerPopupData[];
  personalGifts: EventTriggerPopupData[];
  joinedNewTournament: boolean;
  assignedStampCard?: boolean;
  /** Backend-evaluated: false while wsc is unavailable, so the payment UI hides cash for offers. */
  eventTriggerCashPurchaseEnabled?: boolean;
}

export type DynamicPopupProps<T extends EventTriggerPopupType> = {
  benefit: EventTriggerPayloadType<T>;
  userEventTriggerId: string;
} & DialogProps;

export type EventTriggerDynamicDialogs<T extends EventTriggerPopupType> = {
  type: T;
  benefit: EventTriggerPayloadType<T>;
  userEventTriggerId: string;
  coverAnimationClass?: string;
  currentStep?: number;
  extraData?: EventTriggerPopupExtraData<T>;
  roadState?: RoadUserState;
} & DialogProps;

export interface ClaimFreePackageSocketResponse {
  benefitKey: string;
  nextOrder: number;
  packageId: number;
  playerId: string;
}
